import { $ref, Config } from './generated/telus-light'

import messages from '../messages/config.messages'

const currentConsultationSummaryChildren = [
  $ref('bookingReasonSummary'),
  $ref('appointmentInvitesSummary'),
]

const pastConsultationSummaryChildren = [
  $ref('preconsultationSummary'),
  $ref('bookingReasonSummary'),
  $ref('attachmentsSummary'),
  $ref('carePlanSummary'),
  $ref('appointmentInvitesSummary'),
]

const config: Config = {
  main: {
    module: 'Main',
    options: {
      layout: $ref('layout'),
    },
  },

  layout: {
    module: 'Layout',
    reactElement: true,
    options: {
      header: $ref('header'),
      navigation: $ref('navigation'),
      routes: $ref('routes'),
    },
  },

  routes: {
    module: 'Routes',
    reactElement: true,
    options: {
      defaultRoute: '/consultations',
      routes: [
        {
          path: '/consultations/:date?/:status?',
          route: $ref('schedulePage'),
        },
        {
          path: '/consultation/:id',
          route: $ref('consultationPage'),
        },
        {
          path: '/all-consultations',
          route: $ref('allConsultationsPage'),
        },
        {
          path: '/admin/consultants/:userid/availability',
          route: $ref('adminPortalPage'),
        },
        {
          path: '/experiments',
          route: $ref('experimentsPage'),
        },
      ],
    },
  },

  header: {
    module: 'Header',
    reactElement: true,
    options: {
      scheduleEnabled: true,
      clinicianRatingEnabled: true,
      languageSelectorEnabled: false,
      telusLogo: true,
      ConsultationScheduleItem: $ref('headerScheduleItem'),
      supportOptions: [
        'clinops',
        'clinical',
        'risk-management',
        'technology-help',
      ],
      consultationContext: $ref('consultationContext'),
    },
  },

  headerScheduleItem: {
    module: 'ConsultationScheduleItemPlugin',
    options: {
      hideIncompleteConsultationNotifications: false,
    },
  },

  navigation: {
    module: 'Navigation',
    options: {
      items: [
        $ref('schedulePageNavigationItem'),
        $ref('allConsultationsPageNavigationItem'),
        $ref('adminPortalPageNavigationItem'),
        '---DIVIDER---',
        $ref('linksAndResourcesPlugin'),
        $ref('releaseNotesPlugin'),
      ],
    },
  },

  schedulePage: {
    module: 'SchedulePage',
    options: {
      ConsultationScheduleItem: $ref('consultationScheduleItem'),
    },
    reactElement: true,
  },

  consultationScheduleItem: {
    module: 'ConsultationScheduleItemPlugin',
    options: {
      hideIncompleteConsultationNotifications: true,
      CheckIn: $ref('consultationScheduleItemAppointmentCheckIn'),
    },
  },

  consultationScheduleItemAppointmentCheckIn: {
    module: 'ScheduleCheckInPlugin',
    options: {
      displayLocation: false,
      displayDetailedError: false,
    },
  },

  schedulePageNavigationItem: {
    module: 'SchedulePageNavigationItem',
    options: {
      path: '/consultations',
    },
    reactElement: true,
  },

  allConsultationsPage: {
    module: 'AllConsultationsPage',
    reactElement: true,
  },

  experimentsPage: {
    module: 'ExperimentsPage',
    reactElement: true,
  },

  allConsultationsPageNavigationItem: {
    module: 'AllConsultationsPageNavigationItem',
    options: {
      path: '/all-consultations',
    },
    reactElement: true,
  },

  adminPortalPage: {
    module: 'AdminPortalPage',
    reactElement: true,
  },

  adminPortalPageNavigationItem: {
    module: 'AdminPortalPageNavigationItem',
    options: {
      path: '/admin/consultants',
    },
    reactElement: true,
  },

  consultationPage: {
    module: 'ConsultationLitePage',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      main: [$ref('preparationSectionGroup'), $ref('actionsSectionGroup')],
      providers: [
        $ref('editedConsultationWarningModelProvider'),
        $ref('callStatusReducerModelProvider'),
      ],
      call: $ref('call'),
      context: $ref('consultationProfile'),
      review: $ref('currentConsultationSummary'),
      actions: $ref('consultationActionsPlugin'),
      completeDialog: $ref('completeDialogPlugin'),
      eventBus: $ref('eventBus'),
      incompleteConsultationWarningModal: $ref(
        'incompleteConsultationWarningModal'
      ),
      externalTabsWarningBanner: $ref('externalTabsWarningModal'),
    },
  },

  editedConsultationWarningModelProvider: {
    module: 'EditedConsultationWarningModelProvider',
    options: {
      consultationContext: $ref('consultationContext'),
    },
  },

  callStatusReducerModelProvider: {
    module: 'CallStatusReducerModelProvider',
    options: {
      callMedium: 'voice_and_video',
    },
  },

  callActiveModalPlugin: {
    module: 'CallActiveModalPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      redirectUrl: '/consultation/:id/review',
      stayOnPageUrl: '/consultation/:id',
    },
  },

  consultationActionsPlugin: {
    module: 'ConsultationActionsPlugin',
    reactElement: true,
    options: {
      reviewEnabled: true,
      completeConsultationModel: $ref('completeConsultationModel'),
      consultationContext: $ref('consultationContext'),
      editedConsultationWarningModel: $ref(
        'editedConsultationWarningModelProvider'
      ),
      callStatusReducerModelProvider: $ref('callStatusReducerModelProvider'),
      callActiveModal: $ref('callActiveModalPlugin'),
    },
  },

  call: {
    module: 'CallPlugin',
    reactElement: true,
    options: {
      callStatusReducerModelProvider: $ref('callStatusReducerModelProvider'),
      consultationContext: $ref('consultationContext'),
      callQualityFeedbackEnabled: true,
      callMedium: 'voice_and_video',
      recordingEnabled: false,
      eventBus: $ref('eventBus'),
    },
  },

  consultationProfile: {
    module: 'ConsultationProfilePlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      eventBus: $ref('eventBus'),
      requestedTranslationEnabled: false,
      checkIn: $ref('consultationProfileAppointmentCheckIn'),
      patientRecordDeepLink: $ref('patientRecordDeepLink'),
    },
  },

  patientRecordDeepLink: {
    module: 'PatientRecordDeepLinkPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
    },
  },

  consultationProfileAppointmentCheckIn: {
    module: 'ConsultationCheckInPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      eventBus: $ref('eventBus'),
      displayLocation: true,
      displayDetailedError: true,
    },
  },

  incompleteConsultationWarningModal: {
    module: 'IncompleteConsultationWarningModalPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      hideIncompleteConsultationModal: false,
    },
  },

  externalTabsWarningModal: {
    module: 'ExternalTabsWarningBannerPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      route: '/consultation/:id',
    },
  },

  preparationSectionGroup: {
    module: 'SectionGroupPlugin',
    reactElement: true,
    options: {
      title: messages.preparation_section_group_title,
      titleColor: 'var(--brand-color-purple)',
      borderColor: 'var(--brand-color-purple)',
      children: [
        $ref('bookingReason'),
        $ref('patientTimeline'),
        $ref('patientDetails'),
      ],
    },
  },

  bookingReason: {
    module: 'BookingReasonPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
    },
  },

  patientTimelineConsultationEvent: {
    module: 'TimelineConsultationEventPlugin',
    options: {
      includeActions: true,
    },
  },

  patientTimeline: {
    module: 'PatientTimelinePlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      consultationSummary: $ref('pastConsultationSummary'),
      timelineConsultationEvent: $ref('patientTimelineConsultationEvent'),
      eventTypes: {
        consultationsEnabled: true,
      },
    },
  },

  patientDetails: {
    module: 'PatientDetailsPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      hideIdVerificationEnabled: false,
    },
  },

  actionsSectionGroup: {
    module: 'SectionGroupPlugin',
    reactElement: true,
    options: {
      title: messages.actions_section_group_title,
      titleColor: 'var(--brand-color-orange)',
      borderColor: 'var(--brand-color-orange)',
      children: [$ref('appointmentInvites')],
    },
  },

  appointmentInvites: {
    module: 'AppointmentInvitesPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
    },
  },

  currentConsultationSummary: {
    module: 'CurrentConsultationSummaryPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      childComponents: currentConsultationSummaryChildren,
    },
  },

  pastConsultationSummary: {
    module: 'PastConsultationSummaryPlugin',
    options: {
      childComponents: pastConsultationSummaryChildren,
    },
  },

  appointmentInvitesSummary: {
    module: 'AppointmentInvitesSummaryPlugin',
    options: {
      enableOnlyForGlobalInvites: false,
    },
  },

  preconsultationSummary: {
    module: 'PreconsultationSummaryPlugin',
  },

  bookingReasonSummary: {
    module: 'BookingReasonSummaryPlugin',
  },

  attachmentsSummary: {
    module: 'AttachmentsSummaryPlugin',
  },

  carePlanSummary: {
    module: 'CarePlanSummaryPlugin',
  },

  releaseNotesPlugin: {
    module: 'ReleaseNotesPlugin',
    options: {
      releaseNoteCountryCode: undefined,
    },
    reactElement: true,
  },

  linksAndResourcesPlugin: {
    module: 'LinksAndResourcesPlugin',
    reactElement: true,
  },

  completeConsultationModel: {
    module: 'CompleteConsultationModel',
    options: {
      consultationContext: $ref('consultationContext'),
      extensions: [],
      redirectUrl: '/consultation/:consultationId/review/form',
    },
  },

  completeDialogPlugin: {
    module: 'CompleteDialogPlugin',
    reactElement: true,
    options: {
      consultationContext: $ref('consultationContext'),
      completeConsultationModel: $ref('completeConsultationModel'),
      extensions: [],
    },
  },
}

export default config
